import { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '@/config';
import storage from '@/utils/storage';
import { useAppContext } from '@/context/AppContext';
import { EventSourcePolyfill } from 'event-source-polyfill';

export const usePodStatus = (enabled: boolean) => {
  const [allPodsSSE, setAllPods] = useState<any[]>([]);
  const [access_token, setAccessToken] = useState(() => storage.getAccessToken()); // Fixed: Use getAccessToken instead of getToken

  // Use refs to hold the current EventSource and timers
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { xTeamId: xTeamIdFromTeamApi } = useAppContext();

  // Update access token when it changes in storage
  useEffect(() => {
    const currentToken = storage.getAccessToken();
    if (currentToken !== access_token) {
      setAccessToken(currentToken);
    }
  }, []); // Remove access_token from dependencies to avoid infinite loops

  // Add a separate effect to watch for token changes more reliably
  useEffect(() => {
    const checkTokenPeriodically = setInterval(() => {
      const currentToken = storage.getAccessToken();
      if (currentToken && currentToken !== access_token) {
        setAccessToken(currentToken);
      }
    }, 1000); // Check every second

    return () => clearInterval(checkTokenPeriodically);
  }, [access_token]);

  const establishSSEConnection = useCallback(() => {
    const teamData = storage.getTeam();
    const xTeamIdFromLocalStorage = teamData ? teamData?._id : null;
    const xTeamId = xTeamIdFromTeamApi || xTeamIdFromLocalStorage;

    if (!enabled || !access_token || !xTeamId) {
      return;
    }

    // Close any existing connection using the ref
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const url = `${API_URL}/v2/pod/status/stream`;

    const newEventSource = new EventSourcePolyfill(url, {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'X-Team': xTeamId,
      },
      withCredentials: false,
    });
    eventSourceRef.current = newEventSource;

    newEventSource.onopen = () => {
      // Clear any pending reconnect timer on a successful connection
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    newEventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setAllPods((prevPods) => {
          const existingIndex = prevPods.findIndex((pod) => pod._id === data._id);
          if (existingIndex !== -1) {
            const updatedPods = [...prevPods];
            updatedPods[existingIndex] = data;
            return updatedPods;
          }
          return [...prevPods, data];
        });
      } catch (err) {
        console.warn('Non-JSON SSE message:', event.data, err);
      }
    };

    newEventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      newEventSource.close();
      // Schedule a reconnect after 5 seconds
      reconnectTimerRef.current = setTimeout(establishSSEConnection, 5000);
    };
  }, [enabled, access_token, xTeamIdFromTeamApi]);

  useEffect(() => {
    if (enabled && access_token) {
      establishSSEConnection();
    } else {
      // On disable, clean up the connection and timers
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    }

    return () => {
      // Cleanup on unmount or dependency change
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [establishSSEConnection]); // Simplified dependencies

  const terminateSSEConnection = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  return { allPodsSSE, establishSSEConnection, terminateSSEConnection };
};
